import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ListItem from "@mui/material/ListItem";

import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import AssessmentTwoToneIcon from "@mui/icons-material/AssessmentTwoTone";
import ListAltTwoToneIcon from "@mui/icons-material/ListAltTwoTone";

import useBeeps from "../useBeeps";
import { AntTabs, AntTab, TabPanel, a11yProps } from "../TimelineItem/tabs";
import HistoryTabs from "../TimelineItem/HistoryTabs";
import Chart from "../TimelineItem/Chart";
import BarChart from "../TimelineItem/BarChart";
import Table from "../TimelineItem/Table";
import About from "../TimelineItem/About";
import importAnswers from "../ROM/importAnswers";
import EmptyItem from "../../Components/EmptyItem";
import { GROUP_BY_TIME } from "../importTimeline";
import TimelineText from "../TimelineItem/TimelineText";
import { CircularProgress } from "@mui/material";
import Board from "../../Analyser/Board";
import { boardTypes } from "../../Analyser/moodboardSettings";
import { ExpandButton } from "../../Components/ExpandButton";

const initAnswers = {
  history: [{ time: "", table: [] }],
  historyTable: [],
  table: [],
  importantLabelProps: [],
  lineChart: [{ time: 0 }],
  about: { hasDescription: false },
  questionLists: [{}],
};

function ROM({ event, surveys, filterOnView, initExpanded = false, creator }) {
  const { t } = useTranslation();
  const theme = useTheme();
  // local state
  const [answers, setAnswers] = useState(initAnswers);
  const [selectedBeepIndex, setSelectedBeepIndex] = useState(0);
  const beepTable = answers.history[selectedBeepIndex].table; // answer table of selected beep
  const beepBarChart = beepTable
    .filter((row) => row.isQuantitative)
    .sort((a, b) => b.visPri - a.visPri);

  const [expanded, setExpanded] = useState(initExpanded);
  const [isLoading, setIsLoading] = useState(true);
  const handleExpanded = () => {
    setExpanded((prev) => {
      return !prev;
    });
  };

  const firstSurvey = surveys?.[0] ?? { time: "" };
  const lastSurvey = surveys?.[surveys.length - 1] ?? { time: "" };
  const isSingleResponse = surveys.length === 1;

  const handleSelectedBeepIndex = (index) => {
    setSelectedBeepIndex(index);
  };

  //server state
  const { beeps, isBeepLoading } = useBeeps(
    surveys.map(({ id }) => id),
    expanded && surveys
  );

  // Tabs
  const handleTabChange = (_, newValue) => {
    setTabIndex(newValue);
  };

  const AntTabHistory = {
    id: "AntTabHistory",
    antTabLabel: t("timeline.buttons.responses"),
    tabPanel: (
      <Chart
        variants={["lineChart", "table"]}
        initView={
          answers.table.filter(({ isQuantitative }) => isQuantitative).length >
            0 && answers.history.length > 1
            ? "lineChart"
            : "table"
        }
        chart={
          <Board
            variant={"timeline"}
            rom={answers}
            item={{
              id: "timeline",
              type: boardTypes.timeline,
              itemIds: [event.itemId],
              labelProps: answers.importantLabelProps,
              title: event.title,
              view: "alltime",
              update: false,
            }}
          />
        }
        table={
          <Board
            variant={"timeline"}
            rom={answers}
            item={{
              id: "table",
              type: boardTypes.table,
              itemIds: [event.itemId],
              labelProps: answers.importantLabelProps,
              title: event.title,
              view: "alltime",
              update: false,
            }}
          />
        }
      />
    ),
  };
  const AntTabResponse = {
    id: "AntTabResponse",
    antTabLabel: t("timeline.buttons.response"),
    tabPanel: (
      <Chart
        variants={["barChart", "table"]}
        initView={beepBarChart.length > 0 ? "barChart" : "table"}
        chart={
          <div>
            <HistoryTabs
              history={answers.history}
              selectedBeepIndex={selectedBeepIndex}
              handleSelectedBeepIndex={handleSelectedBeepIndex}
            />
            {beepBarChart.length > 0 ? (
              <BarChart data={beepBarChart} />
            ) : (
              <EmptyItem
                text={t("timeline.placeholders.emptyBarChart")}
                Icon={AssessmentTwoToneIcon}
              />
            )}
          </div>
        }
        table={
          <div>
            <HistoryTabs
              history={answers.history}
              selectedBeepIndex={selectedBeepIndex}
              handleSelectedBeepIndex={handleSelectedBeepIndex}
            />
            {beepTable.length > 0 ? (
              <Table data={beepTable} />
            ) : (
              <EmptyItem
                text={t("timeline.placeholders.emptyTable")}
                Icon={ListAltTwoToneIcon}
              />
            )}
          </div>
        }
      />
    ),
  };
  const AntTabReferences = {
    id: "AntTabReferences",
    antTabLabel: t("common.buttons.about"),
    tabPanel: (
      <About
        about={answers.about}
        questionList={answers.questionLists[selectedBeepIndex]}
      />
    ),
  };
  const antTabs = isSingleResponse
    ? [AntTabResponse, AntTabReferences]
    : [AntTabHistory, AntTabResponse, AntTabReferences];
  const [tabIndex, setTabIndex] = useState(0);
  useEffect(() => {
    const getAntTabIndexById = (id) =>
      antTabs.findIndex((tab) => id === tab.id);

    if (!isBeepLoading) {
      const importedAnswers = importAnswers(beeps);
      const index = importedAnswers.history.findIndex(
        (item) => item.id === event.id
      );

      if (
        isLoading &&
        ([GROUP_BY_TIME].includes(filterOnView) || isSingleResponse)
      ) {
        setTabIndex(getAntTabIndexById("AntTabResponse"));
        setSelectedBeepIndex(index);
      } else if (isLoading) {
        setTabIndex(getAntTabIndexById("AntTabHistory"));
        setSelectedBeepIndex(surveys.length - 1);
      }
      setAnswers(importedAnswers);
      setIsLoading(false);
    }
  }, [beeps, isLoading, isBeepLoading]);

  return (
    <div>
      <ListItem button onClick={handleExpanded}>
        <Box flex="1 1 auto" display="flex" justifyContent="space-between">
          {GROUP_BY_TIME === filterOnView ? (
            <TimelineText
              title={event.title}
              timestamps={{ start: event.time }}
              creator={creator}
            />
          ) : (
            <TimelineText
              title={event.title}
              timestamps={{ start: firstSurvey.time, end: lastSurvey.time }}
              creator={creator}
            />
          )}
          <Box display="flex">
            {expanded && !isSingleResponse ? (
              <Box>
                <Tooltip title={t("common.buttons.analyse")}>
                  <IconButton
                    component={Link}
                    to={{
                      pathname: "/analyser",
                      state: {
                        from: "timeline",
                        itemId: event.itemId,
                        labelProps: answers.importantLabelProps,
                      },
                    }}
                    aria-label={t("common.buttons.analyse")}
                    size="small"
                    sx={{
                      color: "palette.grey[500]",
                      padding: 1,
                    }}
                  >
                    <AssessmentOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}
            <Box>
              <Tooltip
                title={
                  expanded ? t("common.buttons.less") : t("common.buttons.more")
                }
              >
                <ExpandButton expand={expanded} />
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </ListItem>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box flexGrow={1}>
          <Box paddingLeft={2} paddingRight={2}>
            <AntTabs
              value={tabIndex}
              variant="scrollable"
              onChange={handleTabChange}
              aria-label="ant example"
            >
              {antTabs.map((antTab, i) => (
                <AntTab
                  key={"AntTab" + antTab.antTabLabel}
                  label={antTab.antTabLabel}
                  {...a11yProps(i)}
                />
              ))}
            </AntTabs>
          </Box>
          {isLoading ? (
            <div
              style={{
                height: 300,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 8,
              }}
            >
              <CircularProgress size={30} />
            </div>
          ) : (
            antTabs.map((antTab, i) => (
              <TabPanel
                key={"TabPanel" + antTab.antTabLabel}
                value={tabIndex}
                index={i}
                dir={theme.direction}
              >
                {antTab.tabPanel}
              </TabPanel>
            ))
          )}
        </Box>
      </Collapse>
    </div>
  );
}

ROM.propTypes = {
  event: PropTypes.object.isRequired,
};

export default ROM;
